import { AtSign, Bell, CalendarDays, FolderPlus, UserPlus, Users, type LucideIcon } from "lucide-react";

import { m } from "@/paraglide/messages.js";

export const formatRelativeTime = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffMin < 1) return m.notifications_time_just_now();
  if (diffMin < 60) return m.notifications_time_minutes_ago({ minutes: diffMin });
  if (diffHour < 24) return m.notifications_time_hours_ago({ hours: diffHour });
  if (diffDay < 7) return m.notifications_time_days_ago({ days: diffDay });
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export const getNotificationIcon = (type: string): LucideIcon => {
  if (type.startsWith("invite")) return UserPlus;
  if (type.startsWith("mention")) return AtSign;
  if (type.startsWith("project")) return FolderPlus;
  if (type.startsWith("member")) return Users;
  if (type.startsWith("booking.")) return CalendarDays;
  return Bell;
};

export type NotificationTarget =
  /** A page of this app, opened with the router. */
  | { kind: "here"; href: string }
  /** A page of the other app (a coach's own visit as someone's client): a full page load. */
  | { kind: "other_app"; url: string };

/**
 * Where a notification opens. The backend stores an absolute link made by its typed app helpers,
 * so the link says which app it belongs to and this app never routes it to a page it lacks.
 * Anything else, older messages that only kept a path included, opens nothing.
 */
export const notificationTarget = (
  notification: { metadata: Record<string, unknown> },
  origin: string = window.location.origin,
): NotificationTarget | null => {
  const raw = notification.metadata.url;
  if (typeof raw !== "string" || !URL.canParse(raw)) return null;
  const url = new URL(raw);
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  if (url.origin === origin) return { kind: "here", href: `${url.pathname}${url.search}${url.hash}` };
  return { kind: "other_app", url: url.href };
};

export type NotificationGroupKey = "today" | "yesterday" | "week" | "earlier";

const NOTIFICATION_GROUP_ORDER: NotificationGroupKey[] = ["today", "yesterday", "week", "earlier"];

const NOTIFICATION_GROUP_LABELS: Record<NotificationGroupKey, () => string> = {
  today: () => m.notifications_group_today(),
  yesterday: () => m.notifications_group_yesterday(),
  week: () => m.notifications_group_week(),
  earlier: () => m.notifications_group_earlier(),
};

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

export const getNotificationGroup = (dateString: string): NotificationGroupKey => {
  const today = startOfDay(new Date());
  const target = startOfDay(new Date(dateString));
  const dayMs = 86_400_000;
  const dayDiff = Math.round((today - target) / dayMs);

  if (dayDiff <= 0) return "today";
  if (dayDiff === 1) return "yesterday";
  if (dayDiff < 7) return "week";
  return "earlier";
};

/**
 * Splits notifications into ordered date buckets while preserving the original
 * (newest-first) ordering inside each bucket. Empty buckets are dropped.
 */
export const groupNotificationsByDate = <T extends { createdAt: string }>(items: T[]) => {
  const buckets = new Map<NotificationGroupKey, T[]>();

  for (const item of items) {
    const key = getNotificationGroup(item.createdAt);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(item);
    else buckets.set(key, [item]);
  }

  return NOTIFICATION_GROUP_ORDER.filter((key) => buckets.has(key)).map((key) => ({
    key,
    label: NOTIFICATION_GROUP_LABELS[key](),
    items: buckets.get(key) ?? [],
  }));
};
